import { AlertCircle, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { conversationErrorCopy } from './conversationErrorCopy';

export interface ConversationErrorNoticeProps {
  /** The transport error to surface. Mapped to friendly copy via {@link conversationErrorCopy}. */
  error: Error;
  /** Re-issue the last failed turn (wired to `useConversation().retry`). */
  onRetry: () => void;
  /**
   * A retry is in flight. Disables the button to prevent a double-fire. We do
   * NOT show an animated spinner: the global `prefers-reduced-motion` rule does
   * not freeze Tailwind's `animate-spin`, so a static disabled button keeps the
   * notice reduced-motion safe (ANA-194 DoD).
   */
  retrying?: boolean;
  className?: string;
}

/**
 * Compact, accessible inline error + Retry surface for the conversation dock
 * (ANA-194). Rendered in text mode across peek / detailed / focused when the
 * conversation hook reports `phase === 'error'`; voice mode keeps its own error
 * chrome in the input bar, so this is gated to text by the wired container.
 *
 * `role="alert"` (implicit `aria-live="assertive"`) announces the failure to
 * assistive tech the moment it mounts; the icon is decorative (`aria-hidden`).
 * No motion — the row is static and the Retry control is a real button with a
 * ≥44px touch target (WCAG 2.5.5) and the shared focus-visible ring.
 */
export function ConversationErrorNotice({
  error,
  onRetry,
  retrying = false,
  className,
}: ConversationErrorNoticeProps) {
  const { message } = conversationErrorCopy(error);
  return (
    <div
      role="alert"
      data-testid="conversation-error-notice"
      className={cn(
        'flex items-center gap-2.5 rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm',
        className,
      )}
    >
      <AlertCircle aria-hidden className="size-4 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1 text-foreground">{message}</span>
      <Button
        type="button"
        size="sm"
        variant="tertiary"
        icon={RefreshCw}
        onClick={onRetry}
        disabled={retrying}
        className="min-h-[44px] shrink-0"
      >
        Retry
      </Button>
    </div>
  );
}
